import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import HiveMapView from "@/components/HiveMapView";
import { Hexagon, MapPin, Crown, Calendar, Lock, Eye, Globe2 } from "lucide-react";

const pub = axios.create({ baseURL: API });

export default function PublicShare() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    pub.get(`/public/share/${slug}/meta`)
      .then((r) => setMeta(r.data))
      .catch((e) => setError(e.response?.status === 404 ? "This link doesn't exist or was revoked." : "Could not load."))
      .finally(() => setLoading(false));
  }, [slug]);

  const fetchData = async (password) => {
    setError("");
    try {
      const { data } = await pub.get(`/public/share/${slug}`, {
        headers: password ? { "x-share-password": password } : {},
      });
      setData(data);
    } catch (e) {
      if (e.response?.status === 401) setError("Incorrect password.");
      else if (e.response?.status === 410) setError("This link has expired.");
      else setError("Could not load.");
    }
  };

  useEffect(() => {
    if (meta && !meta.has_password) fetchData(null);
  }, [meta]);

  const submitPwd = (e) => { e.preventDefault(); fetchData(pwd); };

  if (loading) return <div className="min-h-screen grid place-items-center bg-stone-50 text-stone-500">Loading…</div>;
  if (error && !meta) return <ErrorScreen msg={error} />;

  if (meta?.has_password && !data) {
    return (
      <PublicLayout meta={meta}>
        <form onSubmit={submitPwd} className="bg-white border border-stone-200 rounded-3xl p-8 max-w-md mx-auto mt-12" data-testid="share-password-gate">
          <div className="flex items-center gap-2 text-amber-700 mb-3">
            <Lock className="w-5 h-5" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Password protected</span>
          </div>
          <h1 className="font-display text-2xl font-medium">Enter password to view</h1>
          <p className="text-sm text-stone-500 mt-1">{meta.owner_name} secured this share with a password.</p>
          <div className="mt-5">
            <Label>Password</Label>
            <Input type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} autoFocus className="mt-1.5" data-testid="share-password-input" />
          </div>
          {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
          <Button type="submit" className="rounded-full w-full mt-5 bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="share-password-submit">Unlock</Button>
        </form>
      </PublicLayout>
    );
  }

  if (!data) return <div className="min-h-screen grid place-items-center bg-stone-50 text-stone-500">Loading…</div>;

  return (
    <PublicLayout meta={meta}>
      {data.scope === "apiary" ? <ApiaryView data={data} /> : <HiveView data={data} />}
    </PublicLayout>
  );
}

function PublicLayout({ meta, children }) {
  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-yellow-400 grid place-items-center">
              <Hexagon className="w-4 h-4 text-amber-900" strokeWidth={2.5} />
            </div>
            <span className="font-display text-sm font-semibold">Hive <span className="text-yellow-500">@</span> Buzzzillion</span>
          </div>
          <div className="text-xs text-stone-500 flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" /> Read-only · shared by {meta?.owner_name}
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8">
        {children}
      </main>
      <footer className="text-center text-xs text-stone-400 py-6">
        Powered by <a href="/" className="text-amber-700 hover:underline">Hive @ Buzzzillion</a>
      </footer>
    </div>
  );
}

function ErrorScreen({ msg }) {
  return (
    <div className="min-h-screen grid place-items-center bg-stone-50 px-4">
      <div className="text-center max-w-md">
        <div className="font-display text-3xl mb-3">Nothing to see here</div>
        <p className="text-stone-600" data-testid="share-error">{msg}</p>
      </div>
    </div>
  );
}

function OwnerBadge({ owner }) {
  return (
    <div className="flex items-center gap-3 mb-6" data-testid="share-owner-badge">
      {owner.avatar_url ? (
        <img src={owner.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover border border-stone-200" />
      ) : (
        <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-800 grid place-items-center font-semibold">
          {(owner.name || "?")[0]?.toUpperCase()}
        </div>
      )}
      <div>
        <div className="font-medium">{owner.name}</div>
        {owner.location && <div className="text-xs text-stone-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {owner.location}</div>}
      </div>
    </div>
  );
}

function HiveView({ data }) {
  const { hive, journals, harvests, owner } = data;
  return (
    <div data-testid="public-hive-view">
      <OwnerBadge owner={owner} />
      <div className="bg-white border border-stone-200 rounded-2xl p-6">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full" style={{ background: hive.color || "#EAB308" }} />
          <h1 className="font-display text-3xl font-medium">{hive.name}</h1>
        </div>
        <div className="flex flex-wrap gap-3 mt-3 text-sm text-stone-500">
          {hive.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {hive.location}</span>}
          {hive.hive_type && <span>{hive.hive_type}</span>}
          {hive.queen_color && <span className="inline-flex items-center gap-1"><Crown className="w-3.5 h-3.5" /> {hive.queen_color}{hive.queen_year ? ` · ${hive.queen_year}` : ""}</span>}
          {hive.yard && <span className="text-xs uppercase tracking-[0.12em] px-2 py-0.5 bg-stone-100 rounded-full">{hive.yard}</span>}
        </div>
        {hive.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {hive.tags.map((t) => <span key={t} className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-900 rounded-full">{t}</span>)}
          </div>
        )}
      </div>

      {harvests.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-xl font-medium mb-3">Harvests</h2>
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {harvests.slice(0, 6).map((h) => (
              <li key={h.id} className="bg-white border border-stone-200 rounded-xl p-4">
                <div className="text-2xl font-display">{h.amount_lb} lb</div>
                <div className="text-xs text-stone-500 mt-1">{h.product_type} · {h.year}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-8">
        <h2 className="font-display text-xl font-medium mb-3">Recent inspections</h2>
        {journals.length === 0 ? (
          <p className="text-sm text-stone-500">No journal entries yet.</p>
        ) : (
          <ul className="space-y-3">
            {journals.slice(0, 10).map((j) => (
              <li key={j.id} className="bg-white border border-stone-200 rounded-xl p-4">
                <div className="flex items-center justify-between gap-2 text-xs uppercase tracking-[0.15em] text-stone-500 mb-2">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {j.date}</span>
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[10px]">{j.mood}</span>
                </div>
                <p className="text-sm text-stone-800 whitespace-pre-wrap">{j.observations}</p>
                {j.photos?.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {j.photos.slice(0, 4).map((p) => <img key={p} src={p} alt="" className="aspect-square object-cover rounded-lg border border-stone-200" />)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ApiaryView({ data }) {
  const { owner, hives, badges } = data;
  // Fuzz coordinates to ~2 decimal places (~1 km) so we don't leak the exact GPS
  // of the apiary on a public page. Still good enough to see the area.
  const fuzz = (n) => Math.round(Number(n) * 100) / 100;
  const pinned = hives
    .filter((h) => h.latitude != null && h.longitude != null)
    .map((h) => ({
      id: h.id,
      name: h.name,
      color: h.color || "#EAB308",
      lat: fuzz(h.latitude),
      lon: fuzz(h.longitude),
      location: h.weather_location_label || h.location || "",
      strength: h.strength,
      yard: h.yard,
    }));

  return (
    <div data-testid="public-apiary-view">
      <OwnerBadge owner={owner} />
      {owner.bio && <p className="text-stone-700 mb-6">{owner.bio}</p>}
      {badges?.length > 0 && (
        <section className="mb-6" data-testid="public-badges">
          <div className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold mb-2">Badges earned</div>
          <ul className="flex flex-wrap gap-2">
            {badges.map((b) => (
              <li key={b.key} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900 text-xs" title={b.description}>
                <span className="text-base">{b.icon}</span>
                <span className="font-medium">{b.name}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {pinned.length > 0 && (
        <section className="mb-6 bg-white border border-stone-200 rounded-2xl p-5" data-testid="public-apiary-map">
          <div className="flex items-center gap-2 mb-3">
            <Globe2 className="w-4 h-4 text-amber-700" />
            <h3 className="font-display text-lg font-medium">Where these bees live</h3>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-stone-400">approximate area</span>
          </div>
          <HiveMapView hives={pinned} height={260} linkable={false} />
        </section>
      )}

      <h2 className="font-display text-2xl font-medium mb-4">{hives.length} hive{hives.length === 1 ? "" : "s"}</h2>
      {hives.length === 0 ? (
        <p className="text-sm text-stone-500">Nothing public yet.</p>
      ) : (
        <ul className="grid sm:grid-cols-2 gap-4">
          {hives.map((h) => (
            <li key={h.id} className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={`public-apiary-hive-${h.id}`}>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ background: h.color || "#EAB308" }} />
                <span className="font-display text-lg font-medium">{h.name}</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-2 text-xs text-stone-500">
                {h.location && <span>{h.location}</span>}
                {h.yard && <span className="px-1.5 py-0.5 bg-stone-100 rounded-full">{h.yard}</span>}
                <span>{h.hive_type}</span>
              </div>
              {h.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {h.tags.map((t) => <span key={t} className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-900 rounded-full">{t}</span>)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
